import { useEffect, useRef, useState } from 'react';
import {
  useCreateAssetCategory,
  useNextAssetCategoryCode,
  useUpdateAssetCategory,
} from '../../../services/httpServices/queries';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { AlertCircle, Save, Tags, X } from 'lucide-react';
import type { AssetCategory, CategoryScope } from '../../../types/asset';

interface AssetCategoryFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Pass a category to edit it, or leave undefined to create a new one. */
  category?: AssetCategory | null;
  /** Which category list this form manages. */
  scope?: CategoryScope;
}

export function AssetCategoryFormModal({
  open,
  onClose,
  category,
  scope = 'ASSET',
}: AssetCategoryFormModalProps) {
  const isEdit = Boolean(category);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [codeTouched, setCodeTouched] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const { data: nextCodeData, isFetching: isFetchingCode } = useNextAssetCategoryCode(open && !isEdit);
  const generatedCode = nextCodeData?.data?.code;

  const createCategory = useCreateAssetCategory();
  const updateCategory = useUpdateAssetCategory();
  const isSubmitting = createCategory.isPending || updateCategory.isPending;

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setCode(category?.code ?? '');
    setDescription(category?.description ?? '');
    setIsActive(category?.isActive ?? true);
    setCodeTouched(Boolean(category));
    setFormError(null);
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [open, category]);

  // Prefill the auto-generated code while the user has not edited it.
  useEffect(() => {
    if (open && !isEdit && !codeTouched && generatedCode) {
      setCode(generatedCode);
    }
  }, [open, isEdit, codeTouched, generatedCode]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) return setFormError('Category name is required');

    // Code is auto-generated but can be overridden; send undefined to let the
    // server generate one when the field is left empty.
    const payload = {
      name: name.trim(),
      code: code.trim() || undefined,
      description: description.trim() || undefined,
      scope,
      isActive,
    };

    const onSuccess = () => onClose();
    const onError = (err: any) =>
      setFormError(
        err?.details?.map((d: any) => d.message).join(', ') || err?.message || 'Failed to save category',
      );

    if (isEdit && category) {
      updateCategory.mutate({ id: category.id, data: payload }, { onSuccess, onError });
    } else {
      createCategory.mutate(payload, { onSuccess, onError });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Tags size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">{isEdit ? 'Edit Category' : 'Add Category'}</h2>
              <p className="text-xs text-text-muted">
                {isEdit
                  ? 'Update the category details'
                  : scope === 'EXPENSE'
                    ? 'Classify operating expenses into a group'
                    : 'Classify fixed assets into a group'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-background rounded-xl transition-colors disabled:opacity-50"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {formError && (
            <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              ref={nameRef}
              label="Name *"
              placeholder={scope === 'EXPENSE' ? 'e.g. Rent' : 'e.g. Medical Equipment'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
            />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  setCodeTouched(true);
                  setCode(e.target.value);
                }}
                placeholder={isFetchingCode && !isEdit ? 'Generating…' : 'Auto-generated'}
                maxLength={50}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text font-mono text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-text-muted">Auto-generated — you can edit it</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-text mb-1">Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="category-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <label htmlFor="category-active" className="text-sm text-text cursor-pointer">
              Active
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {!isSubmitting && <Save size={16} className="mr-2" />}
              {isEdit ? 'Save Changes' : 'Add Category'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
